@if (notProd) {
  <kin-drawer #debugTray width="wide" placement="end">
    <!-- Header with sticky positioning -->
    <div class="sticky top-0 bg-surface-primary z-10 border-b border-border-subtle" slot="header">
      <div class="flex items-center justify-between p-4">
        <div class="flex items-center space-x-3">
          @if (quoteStore.draftToken()) {
            <button (click)="shareDraftUrl()" class="p-2 rounded-lg transition-colors relative group" aria-label="Share Draft URL">
              <ng-icon name="lucideShare" size="1.25em" class="text-blue-600" aria-hidden="true"></ng-icon>
              @if (urlCopiedSignal()) {
                <span class="bg-white absolute left-[120%] -translate-x-1/2 top-full mt-1 px-2 py-1 rounded  text-xs text-green-700 border border-green-200 shadow whitespace-nowrap">URL copied to clipboard</span>
              }
            </button>
          }
          
          <h1 class="text-xl font-semibold text-content-primary">Debug Console</h1>
          <a
            class="mt-[4px] flex items-center gap-1 text-xs transition-colors"
            href="https://kininsurance.atlassian.net/wiki/spaces/AAR/pages/1137278977/Quoting+UI+Debugger+Guide#Basic-Tab-(default)"
            target="_blank"
          >
            What is this?
            <ng-icon name="lucideExternalLink" size="0.75em" aria-hidden="true"></ng-icon>
          </a>
        </div>
      </div>
      
      <!-- Status Bar -->
      <div class="px-4 pb-3 flex items-center gap-4 text-sm flex-wrap">
        <div class="flex items-center space-x-2">
          <span class="text-content-secondary">CIR ID:</span>
          <span class="font-mono bg-surface-secondary px-2 py-0.5 rounded text-content-primary">{{ quoteStore.cirId() }}</span>
        </div>
        <div class="flex items-center space-x-2">
          <span class="text-content-secondary">Quoting:</span>
          @if (userStore.isQuoting()) {
            <span class="inline-flex items-center px-2.5 py-0.5 rounded-lg text-xs font-medium bg-green-100 text-green-800">
              <ng-icon name="lucideCheck" size="0.75em" class="mr-1"></ng-icon>
              Active
            </span>
          } @else {
            <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
              Inactive
            </span>
          }
        </div>
        <div class="flex items-center space-x-2">
          <span class="text-content-secondary">Build:</span>
          <a class="font-mono text-content-primary hover:text-action-primary transition-colors" 
             href="https://app.datadoghq.com/rum/performance-monitoring?query=%40application.id%3Abc4c19ab-44cc-482b-8a1f-8ccd9d1f1083%20version%3A{{ buildVersion }}" 
             target="_blank">{{ buildVersion }}</a>
        </div>
        <div class="border-l border-border-subtle pl-4">
          <span class="font-mono text-content-primary font-medium">{{ _appContext.environment }}</span>
        </div>
      </div>
    </div>

    <kin-tabs>
      <kin-tab slot="tablist">Basic</kin-tab>
      <kin-tab slot="tablist">State</kin-tab>
      <kin-tab slot="tablist">Scenarios</kin-tab>
      <kin-tab slot="tablist">Token</kin-tab>

      <!-- Basic Panel -->
      <kin-tab-panel>
        <div class="p-6 space-y-8">
          <!-- Configuration Section -->
          <div>
            <h2 class="text-lg font-semibold text-content-primary mb-6 flex items-center gap-3 border-b border-border-subtle pb-3">
              <ng-icon name="lucideSettings" size="1.25em" class="text-content-secondary"></ng-icon>
              Configuration
            </h2>
            <div class="space-y-3">
              <!-- Smarty Streets Key -->
              <div class="flex items-center justify-between py-2 border-b border-border-subtle">
                <div class="flex items-center space-x-2">
                  <ng-icon name="lucideMapPin" size="0.875em" class="text-content-tertiary"></ng-icon>
                  <span class="text-sm text-content-secondary">Smarty Streets Key</span>
                </div>
                <span class="font-mono text-sm text-content-primary">{{ _appContext.smartyStreetsKey }}</span>
              </div>

              <!-- FullStory Org ID -->
              <div class="flex items-center justify-between py-2 border-b border-border-subtle">
                <div class="flex items-center space-x-2">
                  <ng-icon name="lucidePlay" size="0.875em" class="text-content-tertiary"></ng-icon>
                  <span class="text-sm text-content-secondary">FullStory Org ID</span>
                </div>
                <span class="font-mono text-sm text-content-primary">{{ _appContext.fullstoryOrgID }}</span>
              </div>

              <!-- Split IO Key -->
              <div class="flex items-center justify-between py-2">
                <div class="flex items-center space-x-2">
                  <ng-icon name="lucideSettings2" size="0.875em" class="text-content-tertiary"></ng-icon>
                  <span class="text-sm text-content-secondary">Split IO Key</span>
                </div>
                <span class="font-mono text-sm text-content-primary">{{ _appContext.splitIOKey }}</span>
              </div>
            </div>
          </div>

          <!-- API Root Configuration -->
          <div>
            <h2 class="text-lg font-semibold text-content-primary mb-6 flex items-center gap-3 border-b border-border-subtle pb-3">
              <ng-icon name="lucideServer" size="1.25em" class="text-content-secondary"></ng-icon>
              API Configuration
            </h2>
            <app-debug-api-root></app-debug-api-root>
          </div>

          <!-- Helpful Links -->
          <div>
            <h2 class="text-lg font-semibold text-content-primary mb-6 flex items-center gap-3 border-b border-border-subtle pb-3">
              <ng-icon name="lucideExternalLink" size="1.25em" class="text-content-secondary"></ng-icon>
              Helpful Links
            </h2>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <a href="https://kininsurance.atlassian.net/wiki/spaces/ENG/pages/63143970/Test+Addresses" 
                 target="_blank" 
                 rel="noopener"
                 class="flex items-center space-x-3 p-3 group">
                  <ng-icon name="lucideMapPin" size="1em" class="text-content-secondary"></ng-icon>
                <span class="text-fs-sm">List of Test Addresses</span>
              </a>
              <button (click)="clearStorage()"
                      class="flex items-center space-x-3 p-3 bg-surface-secondary rounded-lg hover:bg-surface-tertiary transition-colors group text-left">
                  <ng-icon name="lucideTrash2" size="1em"></ng-icon>
                <span class="text-fs-sm">Clear Browser Storage</span>
              </button>
            </div>
          </div>
        </div>
      </kin-tab-panel>

      <!-- State Panel with subtabs -->
      <kin-tab-panel>
        <div class="p-6">
          <div class="flex gap-2 mb-6">
            <button class="button button--secondary button--sm" 
                    [class.button--primary]="stateTab === 'quote'" 
                    (click)="setStateTab('quote')">Quote</button>
            <button class="button button--secondary button--sm" 
                    [class.button--primary]="stateTab === 'user'" 
                    (click)="setStateTab('user')">User</button>
            <button class="button button--secondary button--sm" 
                    [class.button--primary]="stateTab === 'policy'" 
                    (click)="setStateTab('policy')">Policy</button>
          </div>
          
          <div class="space-y-6">
            @if (stateTab === 'quote') {
              <app-debug-quote-draft-state></app-debug-quote-draft-state>
            }
            @if (stateTab === 'user') {
              <div class="space-y-4">
                <div>
                  <h4 class="text-sm font-medium text-content-primary mb-3">User State (Read-only)</h4>
                  <app-debug-user-state [readonly]="true"></app-debug-user-state>
                </div>
                <div>
                  <h4 class="text-sm font-medium text-content-primary mb-3">User State (Editable)</h4>
                  <app-debug-user-state></app-debug-user-state>
                </div>
              </div>
            }
            @if (stateTab === 'policy') {
              <app-debug-policy-state></app-debug-policy-state>
            }
          </div>
        </div>
      </kin-tab-panel>

      <!-- Scenarios Panel -->
      <kin-tab-panel>
        <div class="p-6 space-y-8">
            <app-debug-scenarios class="block"></app-debug-scenarios>
        </div>
      </kin-tab-panel>

      <!-- Token Panel -->
      <kin-tab-panel>
        <div class="p-6">
          <h3 class="text-sm font-medium text-content-primary mb-4 flex items-center gap-2">
            <ng-icon name="lucideKey" size="1em" class="text-content-secondary"></ng-icon>
            Token Management
          </h3>
          <app-debug-resume></app-debug-resume>
        </div>
      </kin-tab-panel>

      
    </kin-tabs>
  </kin-drawer>
}
